import { apiGet, apiGetJson, apiPostJson, apiPatchJson, apiDeleteJson, apiUpload } from '../../../../utils/api';
import { apiErrorMessage } from '../../../../utils/errorMessages';

// The board and the List view share this. Params carry paging, search and the lane
// filter; the response carries real per-status totals so a lane header can show the whole
// truth even when its cards are not all loaded.
export const listConcepts = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) qs.set(k, v);
  });
  const q = qs.toString();
  return apiGetJson(`/training/concepts${q ? `?${q}` : ''}`);
};
// Lane definitions live on the server: a client-side copy is what hid
// approved_for_scheduling from the board, so both views read this instead.
export const listLanes = () => apiGetJson('/training/concepts/lanes');
export const getConcept       = (id) => apiGetJson(`/training/concepts/${id}`);
export const createRequest    = (request) => apiPostJson('/training/requests', { request });
export const searchTrainingClients = (q) => apiGetJson(`/training/clients${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const generateConcept  = (requestId) => apiPostJson(`/training/requests/${requestId}/generate`, {});
export const routeConcept     = (id, to, assigneeId) => apiPostJson(`/training/concepts/${id}/route`, { to, assignee_id: assigneeId || undefined });
export const listReviewAssignees = () => apiGetJson('/training/review_assignees');
export const decideReview     = (reviewId, body) => apiPostJson(`/training/reviews/${reviewId}/decision`, body);
export const proposeSources   = (id) => apiPostJson(`/training/concepts/${id}/source_records`, {});
// Rewrites the concept from its request. Refused once the concept has cleared
// review, which is why the source panel only offers it while it is still editable.
export const regenerateConcept = (id) => apiPostJson(`/training/concepts/${id}/regenerate`, {});
// Re-runs the catalog match. Also re-derives the class target from the fresh verdict,
// leaving a target a human already confirmed alone.
export const rematchConcept   = (id) => apiPostJson(`/training/concepts/${id}/rematch`, {});
// Asks JPRO to work out the class target for a concept that has none: it judges new vs
// revision and, for a new class, names it properly rather than reusing the marketing title.
// Returns a proposal - the development gate still needs a person to confirm it.
export const suggestClassTarget = (id) => apiPostJson(`/training/concepts/${id}/suggest_class_target`, {});
export const setDisposition   = (id, body) => apiPostJson(`/training/concepts/${id}/disposition`, body);
export const setPricing       = (id, body) => apiPatchJson(`/training/concepts/${id}/pricing`, body);
export const createSale       = (id, body) => apiPostJson(`/training/concepts/${id}/sell`, body);
export const createOverride   = (id, body) => apiPostJson(`/training/concepts/${id}/overrides`, body);
export const signOverride     = (id, body) => apiPostJson(`/training/overrides/${id}/sign`, body);
// retry picks up a build that failed or stalled, keeping artifacts that already
// have content. force regenerates everything, edits included, and is deliberately
// not wired to a button.
// script_format and narration_wpm are what the build control collects, and they were
// silently dropped here: the helper destructured only retry and force, so the picker
// changed nothing. Named explicitly rather than spread, so a new option has to be added
// on purpose and cannot be typo'd into oblivion.
export const produceConcept = (id, { retry = false, force = false,
                                     script_format, narration_wpm } = {}) =>
  apiPostJson(`/training/concepts/${id}/produce`, {
    retry: retry || undefined,
    force: force || undefined,
    script_format: script_format || undefined,
    narration_wpm: narration_wpm || undefined,
  });
export const updateArtifact    = (id, body) => apiPatchJson(`/training/production_artifacts/${id}`, body);
export const reviseArtifact    = (id, instruction) => apiPostJson(`/training/production_artifacts/${id}/revise`, { instruction });
export const deleteArtifact    = (id) => apiDeleteJson(`/training/production_artifacts/${id}`);
export const addArtifact       = (productionId, body) => apiPostJson(`/training/productions/${productionId}/artifacts`, body);
export const writeBackProduction = (id) => apiPostJson(`/training/productions/${id}/write_back`, {});
// Abandons a run stuck at "generating". Rotates the run token server-side, so the
// abandoned job cannot come back later and write into the production.
export const cancelGeneration = (id) => apiDeleteJson(`/training/productions/${id}/generation`);
// Re-verifies that every proposed source URL still resolves. Proposed links rot,
// and a 404 is the most common reason a reviewer fails a source.
export const recheckSources = (conceptId) => apiPostJson(`/training/concepts/${conceptId}/recheck_sources`, {});
export const listArtifactImages    = (artifactId) => apiGetJson(`/training/production_artifacts/${artifactId}/images`);
export const generateArtifactImage = (artifactId, body) => apiPostJson(`/training/production_artifacts/${artifactId}/images`, body);
export const saveArtifactImage     = (artifactId, body) => apiPostJson(`/training/production_artifacts/${artifactId}/images/save`, body);
export const approveArtifactImage  = (id) => apiPostJson(`/training/artifact_images/${id}/approve`, {});
export const rejectArtifactImage   = (id) => apiPostJson(`/training/artifact_images/${id}/reject`, {});
export const updateArtifactImage   = (id, body) => apiPatchJson(`/training/artifact_images/${id}`, body);
export const refineArtifactImage   = (id, body) => apiPostJson(`/training/artifact_images/${id}/refine`, body);
export const listBrandAssets       = () => apiGetJson('/training/brand_assets');
export const createBrandAsset      = (formData) => apiUpload('/training/brand_assets', formData);
export const updateBrandAsset      = (id, body) => apiPatchJson(`/training/brand_assets/${id}`, body);
export const deleteBrandAsset      = (id) => apiDeleteJson(`/training/brand_assets/${id}`);

// Downloads a generated artifact image as a file.
//
// Deliberate exception to the "never raw fetch" rule: this is not a Rails API
// JSON endpoint. It is an absolute, unsigned ActiveStorage proxy URL
// (rails_storage_proxy_url) that the browser already loads unauthenticated for
// the <img> thumbnails. apiGet would prepend /api/v1 and mangle it, and a plain
// <a download> is ignored cross-origin, so the blob has to be fetched to force
// a filename.
export const downloadArtifactImage = async (url, name) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = name || 'image.png';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};
export const approveGate      = (conceptId, gate) => apiPostJson(`/training/concepts/${conceptId}/approve_gate`, { gate });

// --- Live-training sessions (internal) ---
export const listSessions        = (conceptId) => apiGetJson(`/training/concepts/${conceptId}/sessions`);
export const createSession       = (conceptId, session) => apiPostJson(`/training/concepts/${conceptId}/sessions`, { session });
export const updateSession       = (id, session) => apiPatchJson(`/training/sessions/${id}`, { session, override: session.override, override_reason: session.override_reason });
export const cancelSession       = (id) => apiDeleteJson(`/training/sessions/${id}`);
export const getSessionRoster    = (id) => apiGetJson(`/training/sessions/${id}/roster`);
export const markAttendance      = (id, body) => apiPostJson(`/training/sessions/${id}/attendance`, body);
export const listInstructors     = () => apiGetJson('/training/instructors');

// --- Scheduling calendar ---
export const getCalendar          = (from, to, filters = {}) => {
  const p = new URLSearchParams({ from, to, ...filters });
  return apiGetJson(`/training/calendar?${p.toString()}`);
};
export const listSchedulableClasses = () => apiGetJson('/training/schedulable_classes');
export const createSessionAny     = (session) => apiPostJson('/training/sessions', { session, override: session.override, override_reason: session.override_reason });
export const completeSession      = (id) => apiPostJson(`/training/sessions/${id}/complete`, {});
export const listClosures         = (year) => apiGetJson(`/training/closures?year=${year}`);
export const addClosure           = (on, label) => apiPostJson('/training/closures', { on, label });
export const removeClosure        = (id) => apiDeleteJson(`/training/closures/${id}`);
export const setBlockWeekends     = (enabled) => apiPostJson('/training/closures/weekends', { enabled });
export const listGateApprovers= () => apiGetJson('/training/gate_approvers');
export const setGateApprover  = (gate, body) => apiPatchJson(`/training/gate_approvers/${gate}`, body);
export const listReviewDelegations = () => apiGetJson('/training/review_delegations');
export const setReviewDelegation   = (review_type, body) => apiPatchJson(`/training/review_delegations/${review_type}`, body);

// Fetches the artifact as a .docx and triggers a browser download. Same file
// write-back deposits to SharePoint, so manual uploads match automation.
export const downloadArtifact = async (id, title, variant) => {
  const res = await apiGet(`/training/production_artifacts/${id}/download${variant ? `?variant=${variant}` : ''}`);
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  // Prefer the server filename, then the passed title (Content-Disposition may not
  // be exposed cross-origin), then a generic fallback.
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const suffix = variant === 'answer_key' ? ' - Answer Key' : variant === 'questions' ? ' - Quiz' : '';
  const fromTitle = title ? `${(title + suffix).replace(/[^A-Za-z0-9 _-]/g, '').trim() || 'artifact'}.docx` : null;
  const name = (match && match[1]) || fromTitle || `artifact-${id}.docx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
export const updateSource     = (id, source_record) => apiPatchJson(`/training/source_records/${id}`, { source_record });
// One request for all six checks, which is what pass-all / fail-all sends.
export const setSourceChecks  = (id, value, extra = {}) => apiPatchJson(`/training/source_records/${id}`, {
  source_record: {
    check_official: value, check_current: value, check_jurisdiction: value,
    check_topic: value, check_effective_date: value, check_conflict: value, ...extra,
  },
});
// Not deleteSource: that one removes a catalog sync source, a different resource.
export const deleteSourceRecord = (id) => apiDeleteJson(`/training/source_records/${id}`);
export const listLibrary      = (params = '') => apiGetJson(`/training/library${params}`);
export const listMembers      = () => apiGetJson('/training/members');
export const addMember        = (user_id, training_role) => apiPostJson('/training/members', { user_id, training_role });
export const removeMember     = (id) => apiDeleteJson(`/training/members/${id}`);
export const searchAssociates = (q) => apiGetJson(`/training/assignable_associates?q=${encodeURIComponent(q)}`);
export const listSyncs        = () => apiGetJson('/admin/training/content_syncs');
export const triggerSync      = () => apiPostJson('/admin/training/content_syncs', {});
export const listCatalogDrafts= () => apiGetJson('/training/catalog/drafts');
export const listConfirmedClasses = () => apiGetJson('/training/catalog/confirmed');
export const confirmClass     = (id, item) => apiPatchJson(`/training/catalog/${id}/confirm`, { item });
export const updateCatalogItem = (id, item) => apiPatchJson(`/training/catalog/${id}`, { item });
export const enrichPending     = (limit) => apiPostJson('/training/catalog/enrich', limit ? { limit } : {});
export const enrichCatalogItem = (id) => apiPostJson(`/training/catalog/${id}/enrich`, {});
export const setCanonicalFile = (id, file_id) => apiPatchJson(`/training/catalog/${id}/canonical`, { file_id });
export const mergeClass       = (id, into_id) => apiPostJson(`/training/catalog/${id}/merge`, { into_id });
export const splitClass       = (id, script_file_ids) => apiPostJson(`/training/catalog/${id}/split`, { script_file_ids });
export const unsplitClass     = (id) => apiPostJson(`/training/catalog/${id}/unsplit`, {});
export const listSources      = () => apiGetJson('/training/catalog/sources');
export const addSourceLink    = (year, share_url) => apiPostJson('/training/catalog/sources', { year, share_url });
export const addSourceUpload  = (year, docx_base64, uploaded_filename) =>
  apiPostJson('/training/catalog/sources', { year, docx_base64, uploaded_filename });
export const syncSource       = (id) => apiPostJson(`/training/catalog/sources/${id}/sync`, {});
export const deleteSource     = (id) => apiDeleteJson(`/training/catalog/sources/${id}`);
export const generateIndexDoc = (year, force = false) => apiPostJson('/training/catalog/index/generate', { year, force });
export const getReconcile     = () => apiGetJson('/training/catalog/reconcile');
export const runReconcile     = () => apiPostJson('/training/catalog/reconcile', {});

// --- Governed Word-doc templates for produced artifacts ---
export const listArtifactTemplates = () => apiGetJson('/training/artifact_templates');
export const upsertArtifactTemplate = async (scope, formData) => {
  const res = await apiUpload(`/training/artifact_templates/${scope}`, formData);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || (body.errors && body.errors.join(', ')) || `Save failed (${res.status})`);
  return body;
};
export const deleteArtifactTemplate = (scope) => apiDeleteJson(`/training/artifact_templates/${scope}`);
export const seedArtifactTemplate = (scope, body = {}) => apiPostJson(`/training/artifact_templates/${scope}/seed`, body);
// Downloads a sample .docx rendered through the saved template, so branding can be
// checked without producing a throwaway class first.
export const previewArtifactTemplate = async (scope) => {
  const res = await apiGet(`/training/artifact_templates/${scope}/preview`);
  if (!res.ok) {
    // Through the shared formatter, not straight to the toast: this endpoint can
    // fail with an internal error, and every other path in the app already routes
    // its message through apiErrorMessage. A 5xx line is shown only when the API
    // marked it user_facing.
    let detail = '';
    let userFacing = false;
    try {
      const body = await res.json();
      detail = body.error || '';
      userFacing = body.user_facing === true;
    } catch { /* binary or empty body */ }
    throw new Error(apiErrorMessage(res.status, detail, { userFacing }));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Template preview - ${scope}.docx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// --- Admin-editable guided-intake prompts ---
export const getIntakePrompts   = () => apiGetJson('/training/intake_prompts');
export const upsertIntakePrompt = (persona, body) => apiPostJson(`/training/intake_prompts/${persona}`, body);
export const seedIntakePrompts  = () => apiPostJson('/training/intake_prompts/seed', {});

// Which view the caller gets: 'internal' | 'limited' | 'client'.
export const getTrainingCapabilities = () => apiGetJson('/training/me');
export const getDashboard = () => apiGetJson('/training/dashboard');

// --- client-facing (limited view) ---
export const listOfferedClasses  = () => apiGetJson('/training/offered_classes');
export const setOfferPricing     = (id, body) => apiPatchJson(`/training/catalog/${id}/offer`, body);
// Sales filters (status/source/q/from/to/page/per_page). Blank values are dropped so
// an untouched filter never narrows the query, and both calls take the SAME filters:
// the tiles and the table have to describe one set of purchases or they contradict.
const salesQuery = (filters = {}) => {
  const p = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) p.append(k, v); });
  const qs = p.toString();
  return qs ? `?${qs}` : '';
};
export const listPurchases       = (filters) => apiGetJson(`/training/purchases${salesQuery(filters)}`);
export const purchaseAnalytics   = (filters) => apiGetJson(`/training/purchases/analytics${salesQuery(filters)}`);
export const buyOfferedClass     = (id, body) => apiPostJson(`/training/client/offered_classes/${id}/checkout`, body);
export const listMyRequests      = () => apiGetJson('/training/client/requests');
export const listMyPurchases     = () => apiGetJson('/training/client/purchases');
export const getMyRequest        = (id) => apiGetJson(`/training/client/requests/${id}`);
export const submitClientRequest = (request) => apiPostJson('/training/client/requests', { request });
export const listOrgMembers     = () => apiGetJson('/training/client/org_members');
export const shareRequest        = (id, user_id) => apiPostJson(`/training/requests/${id}/shares`, { user_id });
export const unshareRequest      = (id, user_id) => apiDeleteJson(`/training/requests/${id}/shares/${user_id}`);
export const clientCheckout      = (conceptId, body) => apiPostJson(`/training/client/concepts/${conceptId}/checkout`, body);
export const listConceptSessions = (conceptId) => apiGetJson(`/training/client/concepts/${conceptId}/sessions`);
export const listPurchaseSessions = (purchaseId) => apiGetJson(`/training/client/purchases/${purchaseId}/sessions`);
export const getMySeats          = () => apiGetJson('/training/client/my_seats');
export const registerSeat        = (sessionId, seat_id) => apiPostJson(`/training/client/sessions/${sessionId}/register`, { seat_id });
export const cancelRegistration  = (registrationId) => apiPostJson(`/training/client/registrations/${registrationId}/cancel`, {});
export const listSeats           = (purchaseId) => apiGetJson(`/training/purchases/${purchaseId}/seats`);
export const assignSeat          = (purchaseId, body) => apiPostJson(`/training/purchases/${purchaseId}/seats`, body);
export const revokeSeat          = (seatId) => apiDeleteJson(`/training/seats/${seatId}`);
export const reassignSeat        = (seatId, body) => apiPatchJson(`/training/seats/${seatId}`, body);
export const resendSeatInvite    = (seatId) => apiPostJson(`/training/seats/${seatId}/resend`, {});

// "Buy again" / add seats: routes to the right checkout for a concept class vs
// a catalog class. `rebuy` comes straight off a purchase payload.
export const rebuyCheckout = (rebuy, seats) => (
  rebuy?.kind === 'catalog'
    ? buyOfferedClass(rebuy.id, { seats, return_url: window.location.href })
    : clientCheckout(rebuy.id, { seats, return_url: window.location.href })
);

// --- orders, receipts, post-checkout confirmation ---
export const listMyOrders        = () => apiGetJson('/training/client/orders');
export const getReceiptUrl       = (purchaseId) => apiGetJson(`/training/client/purchases/${purchaseId}/receipt`);
export const getPurchaseStatus   = (purchaseId) => apiGetJson(`/training/client/purchases/${purchaseId}/status`);

// --- admin-editable Training settings (LMS destination) ---
export const getTrainingSettings    = () => apiGetJson('/training/settings');
export const updateTrainingSettings = (body) => apiPatchJson('/training/settings', body);

// --- A/V script structure editing ---
//
// Every mutating call carries content_rev, the revision the editor loaded. A 409 means
// someone else changed the script and the editor must reload rather than overwrite.
const scriptBase = (artifactId) => `/training/production_artifacts/${artifactId}`;

export const getScriptFormats   = () => apiGetJson('/training/script_formats');
// The class length lives on the concept and its status is locked once development is
// approved, so changing it at build time is its own audited action rather than a PATCH.
export const setClassLength     = (conceptId, minutes) => apiPostJson(`/training/concepts/${conceptId}/class_length`, { minutes });
export const getScriptDocument  = (artifactId) => apiGetJson(`${scriptBase(artifactId)}/script`);

export const addChapter         = (artifactId, body) => apiPostJson(`${scriptBase(artifactId)}/chapters`, body);
export const updateChapter      = (artifactId, cid, body) => apiPatchJson(`${scriptBase(artifactId)}/chapters/${cid}`, body);
// apiDeleteJson takes fetch options, not a body, so content_rev rides in the query.
export const deleteChapter      = (artifactId, cid, content_rev) => apiDeleteJson(`${scriptBase(artifactId)}/chapters/${cid}?content_rev=${content_rev}`);
export const reorderChapters    = (artifactId, body) => apiPostJson(`${scriptBase(artifactId)}/chapters/reorder`, body);
export const regenerateChapter  = (artifactId, cid, body) => apiPostJson(`${scriptBase(artifactId)}/chapters/${cid}/regenerate`, body);

export const updateSlide        = (artifactId, cid, sid, body) => apiPatchJson(`${scriptBase(artifactId)}/chapters/${cid}/slides/${sid}`, body);
export const deleteSlide        = (artifactId, cid, sid, content_rev) => apiDeleteJson(`${scriptBase(artifactId)}/chapters/${cid}/slides/${sid}?content_rev=${content_rev}`);
export const splitSlide         = (artifactId, cid, sid, body) => apiPostJson(`${scriptBase(artifactId)}/chapters/${cid}/slides/${sid}/split`, body);
export const mergeSlideUp       = (artifactId, cid, sid, body) => apiPostJson(`${scriptBase(artifactId)}/chapters/${cid}/slides/${sid}/merge_up`, body);
export const moveSlide          = (artifactId, cid, sid, body) => apiPostJson(`${scriptBase(artifactId)}/chapters/${cid}/slides/${sid}/move`, body);

export const addKnowledgeCheck        = (artifactId, cid, body) => apiPostJson(`${scriptBase(artifactId)}/chapters/${cid}/knowledge_check`, body);
export const updateKnowledgeCheck     = (artifactId, cid, body) => apiPatchJson(`${scriptBase(artifactId)}/chapters/${cid}/knowledge_check`, body);
export const deleteKnowledgeCheck     = (artifactId, cid, content_rev) => apiDeleteJson(`${scriptBase(artifactId)}/chapters/${cid}/knowledge_check?content_rev=${content_rev}`);
export const regenerateKnowledgeCheck = (artifactId, cid, body) => apiPostJson(`${scriptBase(artifactId)}/chapters/${cid}/knowledge_check/regenerate`, body);
export const regenerateAnswerKeys     = (artifactId, cid, body) => apiPostJson(`${scriptBase(artifactId)}/chapters/${cid}/knowledge_check/answer_keys`, body);

export const regenerateClosing  = (artifactId, body) => apiPostJson(`${scriptBase(artifactId)}/closing/regenerate`, body);
export const anchorScriptImage  = (artifactId, imageId, body) => apiPostJson(`${scriptBase(artifactId)}/images/${imageId}/anchor`, body);

// Caption and alt text suggested from the picture itself. Returned, never stored: the
// author accepts, edits or ignores them.
export const captionImageVariant = (artifactId, body) => apiPostJson(`/training/production_artifacts/${artifactId}/images/caption`, body);
export const captionSavedImage   = (imageId) => apiPostJson(`/training/artifact_images/${imageId}/caption`, {});
